import * as React from 'react';

import {
  enumLoadStatus,
  enumPurchaseLoadStatus,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import {
  cn,
  Col,
  Kbd,
  Row,
  SearchField,
  Switch,
  Toggle,
  ToggleButtonGroup,
  TouchButton,
} from '@rematter/ui-web';
import { LucideRefreshCw } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

import { AccountFilterPopover } from './AccountFilterPopover';

import { TicketListFilter } from '~/modules/trade/types/scaleTickets';
import { TouchSelectEnum } from '~/shared/components/select-fields/TouchSelectEnum';

const orderedPurchaseLoadStatus = {
  Open: enumPurchaseLoadStatus.Open,
  InventoryComplete: enumPurchaseLoadStatus.InventoryComplete,
  PriceComplete: enumPurchaseLoadStatus.PriceComplete,
  Paid: enumPurchaseLoadStatus.Paid,
  Held: enumPurchaseLoadStatus.Held,
  Voided: enumPurchaseLoadStatus.Voided,
};

type TicketListFiltersProps = {
  layout: 'list' | 'sidebar';
  direction: 'inbound' | 'outbound';
  filter: TicketListFilter;
  onSearchChange: (value: string | null) => void;
  onStatusChange: (value?: string) => void;
  onStatusToggle: (status: string) => void;
  onAccountToggle: (id: string, isSelected: boolean) => void;
  onClearAccounts: () => void;
  mineOnly?: boolean;
  onMineOnlyChange?: (value: boolean) => void;
  onCreateNew?: () => void;
  isCreating?: boolean;
  onRefresh?: () => void;
  searchTrailing?: React.ReactNode;
  bottomTrailing?: React.ReactNode;
  className?: string;
};

type RefreshButtonProps = {
  'aria-label'?: string;
  onRefresh: () => void;
};

//
// ⚛️ ⚛️ ⚛️ REFRESH BUTTON ⚛️ ⚛️ ⚛️
//
const RefreshButton = ({
  'aria-label': ariaLabel,
  onRefresh,
}: RefreshButtonProps) => (
  <TouchButton
    variant="outline"
    className="@RefreshButton h-13 max-w-12 p-3"
    aria-label={ariaLabel}
    onPress={onRefresh}
  >
    <LucideRefreshCw size={16} />
  </TouchButton>
);

type CreateTicketButtonProps = {
  onCreateNew: () => void;
  isCreating?: boolean;
  className?: string;
};

//
// ⚛️ ⚛️ ⚛️ CREATE TICKET BUTTON ⚛️ ⚛️ ⚛️
//
const CreateTicketButton = ({
  onCreateNew,
  isCreating,
  className,
}: CreateTicketButtonProps) => {
  const t = useTranslations();

  return (
    <TouchButton
      variant="outline"
      color="primary"
      size="xl"
      className={cn('@CreateTicketButton', className)}
      onPress={onCreateNew}
      isDisabled={isCreating}
    >
      <Row gap1>
        <Kbd variant="outline" shortcut="Alt+T" />
        {isCreating ? t.common.loading : t.scaleTicketPro.newTicket}
      </Row>
    </TouchButton>
  );
};

type MineOnlySwitchProps = {
  size: 'sm' | 'lg';
  isSelected?: boolean;
  onChange?: (value: boolean) => void;
};

//
// ⚛️ ⚛️ ⚛️ MINE ONLY SWITCH ⚛️ ⚛️ ⚛️
//
const MineOnlySwitch = ({
  size,
  isSelected,
  onChange,
}: MineOnlySwitchProps) => {
  const t = useTranslations();

  return (
    <Row
      yCenter
      className={cn(
        '@MineOnlySwitch',
        cn(
          'rounded-md border-2 border-neutral/20 bg-white',
          size === 'lg' && 'h-13 min-w-27 p-2',
          size === 'sm' && 'h-9 min-w-27 p-1.25',
        ),
      )}
    >
      <Switch isSelected={isSelected} onChange={onChange}>
        <span
          className={cn(
            size === 'lg' && 'ml-2 text-base',
            size === 'sm' && 'text-[11px]',
          )}
        >
          {t.scaleTicketPro.mineOnly}
        </span>
      </Switch>
    </Row>
  );
};

//
// ⚛️ ⚛️ ⚛️ INBOUND LIST FILTERS ⚛️ ⚛️ ⚛️
//
const InboundListFilters = ({
  filter,
  onSearchChange,
  onStatusChange,
  onAccountToggle,
  onClearAccounts,
  mineOnly,
  onMineOnlyChange,
  onCreateNew,
  isCreating,
  onRefresh,
}: TicketListFiltersProps) => {
  const t = useTranslations();

  return (
    <>
      <SearchField
        size="xl"
        className="max-w-64 text-base"
        aria-label={t.scaleTicketPro.searchTickets}
        placeholder={t.scaleTicketPro.search}
        value={filter.searchTerm}
        onChange={onSearchChange}
      />

      <MineOnlySwitch
        size="lg"
        isSelected={mineOnly}
        onChange={onMineOnlyChange}
      />

      <TouchSelectEnum
        variant="outlined"
        size="xl"
        showChevron
        hideSearch
        aria-label={t.scaleTicketPro.filterByStatus}
        value={filter.statuses[0] ?? 'all'}
        onChange={onStatusChange}
        className="max-w-50"
        enumValue={orderedPurchaseLoadStatus}
      />

      <AccountFilterPopover
        direction="inbound"
        selectedIds={filter.accountIds}
        onToggle={onAccountToggle}
        onClearAll={onClearAccounts}
      />

      {onCreateNew && (
        <CreateTicketButton onCreateNew={onCreateNew} isCreating={isCreating} />
      )}
      {onRefresh && (
        <RefreshButton
          aria-label={t.scaleTicketPro.refresh}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

type OutboundStatusToggleProps = {
  statuses: string[];
  onStatusToggle: (status: string) => void;
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND STATUS TOGGLE ⚛️ ⚛️ ⚛️
//
const OutboundStatusToggle = ({
  statuses,
  onStatusToggle,
}: OutboundStatusToggleProps) => {
  const t = useTranslations();

  const labelByLoadStatus = React.useMemo<Record<LoadStatus | string, string>>(
    () => ({
      [enumLoadStatus.ContainerReadyToShip]:
        t.scaleTicketPro.statusContainerReadyToShip,
      [enumLoadStatus.Shipped]: t.scaleTicketPro.statusShipped,
      [enumLoadStatus.Voided]: t.scaleTicketPro.statusVoided,
    }),
    [t],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      const newStatuses = [...keys] as string[];
      const added = newStatuses.find((s) => !statuses.includes(s));
      const removed = statuses.find((s) => !newStatuses.includes(s));
      const toggled = added ?? removed;
      if (toggled) onStatusToggle(toggled);
    },
    [statuses, onStatusToggle],
  );

  return (
    <ToggleButtonGroup
      aria-label="toggle ticket status"
      selectionMode="multiple"
      selectedKeys={statuses}
      onSelectionChange={handleSelectionChange}
      className="@OutboundStatusToggle"
    >
      {Object.entries(labelByLoadStatus).map(([key, status]) => (
        <Toggle key={key} id={key} size="xl">
          {status}
        </Toggle>
      ))}
    </ToggleButtonGroup>
  );
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND LIST FILTERS ⚛️ ⚛️ ⚛️
//
const OutboundListFilters = ({
  filter,
  onSearchChange,
  onStatusToggle,
  onAccountToggle,
  onClearAccounts,
  onCreateNew,
  isCreating,
  onRefresh,
}: TicketListFiltersProps) => {
  const t = useTranslations();

  return (
    <>
      <SearchField
        size="xl"
        aria-label={t.scaleTicketPro.searchTickets}
        placeholder={t.scaleTicketPro.search}
        value={filter.searchTerm}
        onChange={onSearchChange}
        className="max-w-64"
      />

      <OutboundStatusToggle
        statuses={filter.statuses}
        onStatusToggle={onStatusToggle}
      />
      <AccountFilterPopover
        direction="outbound"
        selectedIds={filter.accountIds}
        onToggle={onAccountToggle}
        onClearAll={onClearAccounts}
      />

      {onCreateNew && (
        <CreateTicketButton onCreateNew={onCreateNew} isCreating={isCreating} />
      )}
      {onRefresh && (
        <RefreshButton
          aria-label={t.scaleTicketPro.refresh}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ SIDEBAR FILTERS ⚛️ ⚛️ ⚛️
//
const SidebarFilters = ({
  direction,
  filter,
  onSearchChange,
  onStatusChange,
  onAccountToggle,
  onClearAccounts,
  mineOnly,
  onMineOnlyChange,
  onCreateNew,
  isCreating,
  onRefresh,
  searchTrailing,
  bottomTrailing,
}: TicketListFiltersProps) => {
  const t = useTranslations();
  const isInbound = direction === 'inbound';

  return (
    <Col gap2 className="@SidebarFilters border-b border-neutral/20 p-2">
      <Row gap1>
        <SearchField
          aria-label={t.scaleTicketPro.searchTickets}
          placeholder={t.scaleTicketPro.search}
          value={filter.searchTerm}
          onChange={onSearchChange}
          size="xl"
          suffix={<Kbd variant="outline" shortcut="Meta+1" />}
        />
        {onRefresh && (
          <RefreshButton
            aria-label={t.scaleTicketPro.refresh}
            onRefresh={onRefresh}
          />
        )}
        {searchTrailing}
      </Row>

      <Row gap1>
        <MineOnlySwitch
          size="sm"
          isSelected={mineOnly}
          onChange={onMineOnlyChange}
        />
        <TouchSelectEnum
          size="xs"
          variant="outlined"
          showChevron
          hideSearch
          aria-label={t.scaleTicketPro.filterByStatus}
          enumValue={isInbound ? orderedPurchaseLoadStatus : enumLoadStatus}
          value={filter.statuses[0] ?? 'all'}
          onChange={onStatusChange}
        />
      </Row>

      <Row gap1>
        {bottomTrailing}
        {onCreateNew && (
          <CreateTicketButton
            onCreateNew={onCreateNew}
            isCreating={isCreating}
            className="flex-1"
          />
        )}
        <AccountFilterPopover
          direction={direction}
          selectedIds={filter.accountIds}
          onToggle={onAccountToggle}
          onClearAll={onClearAccounts}
        />
      </Row>
    </Col>
  );
};

//
// ⚛️ ⚛️ ⚛️ TICKET LIST FILTERS ⚛️ ⚛️ ⚛️
//
export const TicketListFilters = (props: TicketListFiltersProps) => {
  if (props.layout === 'sidebar') {
    return <SidebarFilters {...props} />;
  }

  if (props.direction === 'inbound') {
    return <InboundListFilters {...props} />;
  }

  return <OutboundListFilters {...props} className="@TicketListFilters" />;
};
